import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const LOOKBOOK_IMG =
  "https://images.unsplash.com/photo-1635650805000-f31dee3133bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjB1cmJhbnxlbnwwfHx8fDE3ODQzMTI4ODl8MA&ixlib=rb-4.1.0&q=85";

// Hotspots auto-map to the first live products
const HOTSPOTS = [
  { id: "top", top: "34%", left: "52%", index: 0 },
  { id: "bottom", top: "72%", left: "46%", index: 1 },
];

export default function InteractiveClothing({ products }) {
  const [active, setActive] = useState(null);
  const { addItem } = useCart();

  return (
    <section
      id="lookbook"
      data-testid="interactive-clothing"
      className="relative py-24 md:py-32 bg-black"
    >
      <div className="px-6 md:px-12 mb-12">
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-steel-light mb-3">
          03 — Field Study
        </p>
        <h2 className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter text-white max-w-2xl leading-[0.9]">
          Tap the garment. Read the echo.
        </h2>
      </div>

      <div className="px-6 md:px-12">
        <div className="relative w-full max-w-4xl mx-auto aspect-[3/4] md:aspect-[16/10] overflow-hidden border border-white/10 group">
          <img
            src={LOOKBOOK_IMG}
            alt="Ghostline lookbook"
            className="absolute inset-0 w-full h-full object-cover saturate-[0.55] group-hover:scale-[1.02] transition-transform duration-[1200ms] ease-out"
          />
          <div className="absolute inset-0 bg-black/20" />

          {HOTSPOTS.map((h) => {
            const product = products[h.index];
            if (!product) return null;
            const isActive = active === h.id;
            return (
              <div key={h.id} style={{ top: h.top, left: h.left }} className="absolute">
                <button
                  data-testid={`hotspot-${h.id}`}
                  onClick={() => setActive(isActive ? null : h.id)}
                  aria-label={`View ${product.name}`}
                  className="relative -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                >
                  <span className="absolute w-10 h-10 rounded-full border border-white/50 animate-ping opacity-40" />
                  <span className="relative w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors duration-300">
                    {isActive ? (
                      <X className="w-4 h-4 text-black" />
                    ) : (
                      <Plus className="w-4 h-4 text-black" />
                    )}
                  </span>
                </button>

                <AnimatePresence>
                  {isActive && (
                    <ProductCard
                      product={product}
                      left={h.left}
                      onAdd={(size, color) => {
                        addItem(product, size, color);
                        toast.success(`${product.name} added`, {
                          description: `${size} · ${color}`,
                        });
                        setActive(null);
                      }}
                    />
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProductCard({ product, left, onAdd }) {
  const [size, setSize] = useState(product.sizes[2] || product.sizes[0]);
  const [color, setColor] = useState(product.colors[0]);
  const alignRight = parseFloat(left) > 55;

  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, scale: 0.92, x: alignRight ? 10 : -10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`absolute z-20 w-64 bg-black/70 backdrop-blur-xl border border-white/15 p-5 ${
        alignRight ? "right-6 -translate-y-1/2" : "left-6 -translate-y-1/2"
      }`}
      style={{ top: 0 }}
    >
      <p className="font-mono-tech text-[10px] uppercase tracking-widest text-steel-light mb-1">
        {product.category}
      </p>
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="font-display text-xl font-bold text-white">{product.name}</h4>
        <span className="font-mono-tech text-sm text-white">
          ${product.price.toFixed(0)}
        </span>
      </div>
      <p className="text-xs text-steel-light leading-relaxed mb-4">
        {product.description}
      </p>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {product.sizes.map((s) => (
          <button
            key={s}
            data-testid={`card-size-${product.id}-${s}`}
            onClick={() => setSize(s)}
            className={`font-mono-tech text-[10px] px-2 py-1 border transition-colors duration-200 ${
              size === s
                ? "bg-white text-black border-white"
                : "text-white/80 border-white/25 hover:border-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {product.colors.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            className={`font-mono-tech text-[9px] uppercase px-1.5 py-1 transition-colors duration-200 ${
              color === c ? "text-white" : "text-steel-light hover:text-white"
            }`}
          >
            {color === c ? `[${c}]` : c}
          </button>
        ))}
      </div>

      <button
        data-testid={`card-add-${product.id}`}
        onClick={() => onAdd(size, color)}
        className="group relative w-full overflow-hidden border border-white bg-white text-black py-2.5"
      >
        <span className="relative z-10 font-mono-tech text-[11px] uppercase tracking-widest group-hover:text-white transition-colors duration-300">
          Add to Cart
        </span>
        <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
      </button>
    </motion.div>
  );
}
